import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../App"; // Import from App.js
import DashboardHeader from "../components/app_components/DashboardHeader";
import DashboardSidebar from "../components/app_components/DashboardSidebar";
import TotalExpensesCard from "../components/app_components/TotalExpensesCard";
import SubscriptionList from "../components/app_components/SubscriptionList";
import SubscriptionCollector from "../components/app_components/SubscriptionCollector";
import DonutChart from "../components/app_components/analytics/DonutChart";
import PriceRadar from "../components/app_components/analytics/PriceRadar";  
import FreeTrialList from "../components/app_components/FreeTrialList";
import TrackedPromoMerchantsList from "../components/app_components/TrackedPromoMerchantsList";
import { Home, Bell, BellDot, ShoppingBag, Settings, Users, ShoppingCart, Share2, Loader2 } from "lucide-react";
import { useTranslation } from '../contexts/LanguageContext';

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user, logout, validateToken, httpClient } = useAuth(); // Get httpClient from auth context
  
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showSubscriptionCollector, setShowSubscriptionCollector] = useState(false);
  const [merchants, setMerchants] = useState([]);
  const [userSubscriptions, setUserSubscriptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [priceData, setPriceData] = useState([]);
  const [freeTrials, setFreeTrials] = useState([]);
  const [isCollectorLoading, setIsCollectorLoading] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [trackedPromoMerchants, setTrackedPromoMerchants] = useState([]);
  const [estimatedExpense, setEstimatedExpense] = useState({
    net_adjustment: 0,
    total_savings: 0,
    total_additional_cost: 0,
    is_net_saving: false,
    spaces_count: 0,
    summary: ''
  });
  const [premiumStatus, setPremiumStatus] = useState({
    isPremium: false,
    userPlan: 'free',
    loading: true
  });
  const hasLoadedRef = useRef(false);

  // Create refs for functions that will be used in event listeners
  const checkUserSubscriptionsRef = useRef();
  const fetchFreeTrialsRef = useRef();
  const fetchTrackedPromoMerchantsRef = useRef();
  const fetchEstimatedExpenseRef = useRef();
  const fetchPriceDataRef = useRef();
  const fetchUnreadNotificationsCountRef = useRef();
  const fetchPremiumStatusRef = useRef();

  // Navigation items
  const navItems = [
    { icon: Home, label: t('partners.nav.home'), path: "/dashboard" },
    { 
      icon: unreadCount > 0 ? BellDot : Bell,
      label: t('partners.nav.notifications'),
      path: "/notifications",
      badge: unreadCount > 0 ? unreadCount : null
    },
    { icon: ShoppingCart, label: t('partners.nav.marketplace'), path: "/marketplace" },
    { icon: Share2, label: t('bottomNav.shareSubs'), path: "/share-subs" },
    { icon: ShoppingBag, label: t('partners.nav.connectBank'), path: "/connectBank" },
    { icon: Users, label: t('partners.nav.partners'), path: "/partners" },
    { icon: Settings, label: t('partners.nav.settings'), path: "/account-settings" },
  ];

  // Fetch premium status from API
  const fetchPremiumStatus = async () => {
    try {
      if (!httpClient) {
        setPremiumStatus({
          isPremium: false,
          userPlan: 'free',
          loading: false
        });
        return;
      }
      
      const response = await httpClient.request('/api/user/premium-status');
      
      if (!response.ok) {
        console.error(`Premium status fetch failed with status: ${response.status}`);
        setPremiumStatus({
          isPremium: false,
          userPlan: 'free',
          loading: false
        });
        return;
      }
      
      const data = await response.json();
      setPremiumStatus({
        isPremium: data.isPremium,
        userPlan: data.userPlan,
        loading: false
      });
    } catch (err) {
      console.error('Error fetching premium status:', err);
      setPremiumStatus({
        isPremium: false,
        userPlan: 'free',
        loading: false
      });
    }
  };

  // Check if initial setup is completed
  const checkInitialSetupStatus = async () => {
    try {
      if (!httpClient) return false;
      
      const response = await httpClient.request('/api/check-initial-setup');
      
      if (!response.ok) {
        console.log(`Setup check failed with status: ${response.status}`);
        return false;
      }
      
      const data = await response.json();
      
      if (data && typeof data.completed === 'boolean') {
        return data.completed;
      }
      
      return false;
    } catch (err) {
      console.error('Error checking initial setup:', err);
      return false;
    }
  };

  // Fetch unread notifications count
  const fetchUnreadNotificationsCount = async () => {
    try {
      if (!httpClient) return;
      
      const response = await httpClient.request('/api/notifications');
      
      if (!response.ok) {
        throw new Error('Failed to fetch notifications');
      }

      const notifications = await response.json();
      const unread = notifications.filter(notif => !notif.read_status).length;
      setUnreadCount(unread);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    }
  };

  // Fetch price radar data
  const fetchPriceData = async () => {
    try {
      if (!httpClient) return;
      
      const response = await httpClient.request('/api/price-changes');
      
      if (response.ok) {
        const data = await response.json();
        setPriceData(data);
      } else {
        setPriceData([]);
      }
    } catch (err) {
      console.error('Error fetching price data:', err);
      setPriceData([]);
    }
  };

  // Fetch estimated expense adjustment from sharing spaces
  const fetchEstimatedExpense = async () => {
    try {
      if (!httpClient) return;
      
      const response = await httpClient.request('/api/sharing-space/user/estimated-expense');
      
      if (response.ok) {
        const data = await response.json();
        console.log("Estimated expense data:", data);
        
        setEstimatedExpense(data.estimated_expense || {
          net_adjustment: 0,
          total_savings: 0,
          total_additional_cost: 0,
          is_net_saving: false,
          spaces_count: 0,
          summary: ''
        });
      } else {
        console.error('Failed to fetch estimated expense');
        setEstimatedExpense({
          net_adjustment: 0,
          total_savings: 0,
          total_additional_cost: 0,
          is_net_saving: false,
          spaces_count: 0,
          summary: ''
        });
      }
    } catch (error) {
      console.error('Error fetching estimated expense:', error);
      setEstimatedExpense({
        net_adjustment: 0,
        total_savings: 0,
        total_additional_cost: 0,
        is_net_saving: false,
        spaces_count: 0,
        summary: ''
      });
    }
  };

  const fetchMerchants = async () => {
    setIsCollectorLoading(true);
    try {
      if (!httpClient) {
        setIsCollectorLoading(false);
        return;
      }
      
      const isSetupComplete = await checkInitialSetupStatus();
      
      if (isSetupComplete) {
        console.log("Initial setup already completed, hiding collector");
        setShowSubscriptionCollector(false);
        setIsCollectorLoading(false);
        return;
      }

      const response = await httpClient.request('/api/popular-merchant');
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (!data || !data.merchants || !Array.isArray(data.merchants)) {
        setMerchants([]);
        setShowSubscriptionCollector(false);
        setIsCollectorLoading(false);
        return;
      }
      
      const validMerchants = data.merchants.filter(merchant => 
        merchant && merchant.id && merchant.name
      );
      
      if (validMerchants.length === 0) {
        setShowSubscriptionCollector(false);
        setIsCollectorLoading(false);
        return;
      }
      
      setMerchants(validMerchants);
      setShowSubscriptionCollector(true);
    } catch (err) {
      setMerchants([]);
      setShowSubscriptionCollector(false);
    } finally {
      setIsCollectorLoading(false);
    }
  };

  const checkUserSubscriptions = async () => {
    try {
      if (!httpClient) return;
      
      const response = await httpClient.request('/api/subscription-list');
      
      // Handle token expiration
      if (response.status === 401) {
        const revalidated = await validateToken();
        if (!revalidated) {
          navigate("/demo");
          return;
        }
        // Retry the request
        return checkUserSubscriptions();
      }
      
      const data = await response.json();
      
      if (Array.isArray(data)) {
        setUserSubscriptions(data);
      }
    } catch (error) {
      console.error('Error checking subscriptions:', error);
      if (error.status === 401) {
        navigate("/demo");
      }
    }
  };

  const fetchFreeTrials = async () => {
    try {
      if (!httpClient) return;
      
      const response = await httpClient.request('/api/free-trial-list');
      
      if (response.ok) {
        const data = await response.json();
        setFreeTrials(data);
      }
    } catch (error) {
      console.error('Error fetching free trials:', error);
    }
  };

  // Fetch tracked promo merchants
  const fetchTrackedPromoMerchants = async () => {
    try {
      if (!httpClient) return;
      
      console.log('Fetching tracked merchants');
      
      const response = await httpClient.request('/api/user-tracked-merchants');
      
      console.log('Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Tracked merchants data:', data);
        setTrackedPromoMerchants(data);
      } else {
        const errorText = await response.text();
        console.error('Failed to fetch tracked merchants:', response.status, errorText);
        setTrackedPromoMerchants([]);
      }
    } catch (error) {
      console.error('Error fetching tracked promo merchants:', error);
      setTrackedPromoMerchants([]);
    }
  };

  // Refresh all dashboard data
  const refreshAllData = async () => {
    try {
      console.log('Refreshing all dashboard data...');
      await Promise.all([
        checkUserSubscriptions(),
        fetchFreeTrials(),
        fetchTrackedPromoMerchants(),
        fetchEstimatedExpense(),
        fetchPriceData(),
        fetchUnreadNotificationsCount(),
        fetchPremiumStatus()
      ]);
      console.log('Dashboard data refreshed successfully');
    } catch (error) {
      console.error('Error refreshing dashboard data:', error);
    }
  };

  // Update refs with the latest function instances on every render
  useEffect(() => {
    checkUserSubscriptionsRef.current = checkUserSubscriptions;
    fetchFreeTrialsRef.current = fetchFreeTrials;
    fetchTrackedPromoMerchantsRef.current = fetchTrackedPromoMerchants;
    fetchEstimatedExpenseRef.current = fetchEstimatedExpense;
    fetchPriceDataRef.current = fetchPriceData;
    fetchUnreadNotificationsCountRef.current = fetchUnreadNotificationsCount;
    fetchPremiumStatusRef.current = fetchPremiumStatus;
  });

  // Event listener for subscription updates
  useEffect(() => {
    const handleSubscriptionsUpdated = () => {
      console.log('Subscriptions updated event received, refreshing dashboard data...');
      
      // Use the refs to call the latest version of each function
      Promise.all([
        checkUserSubscriptionsRef.current?.(),
        fetchFreeTrialsRef.current?.(),
        fetchTrackedPromoMerchantsRef.current?.(),
        fetchEstimatedExpenseRef.current?.(),
        fetchPriceDataRef.current?.(),
        fetchUnreadNotificationsCountRef.current?.(),
        fetchPremiumStatusRef.current?.()
      ]).catch(error => {
        console.error('Error in subscription update handler:', error);
      });
    };

    window.addEventListener('subscriptionsUpdated', handleSubscriptionsUpdated);
    
    return () => {
      window.removeEventListener('subscriptionsUpdated', handleSubscriptionsUpdated);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    
    // Redirect to mobile dashboard if on mobile
    if (isMobile) {
      navigate("/mobile-dashboard");
    }
    
    // Load data if user is authenticated
    if (user && !hasLoadedRef.current) {
      setIsLoading(true);
      setIsCollectorLoading(true);
      hasLoadedRef.current = true;
      
      const loadData = async () => {
        try {
          await Promise.all([
            fetchMerchants(),
            checkUserSubscriptions(),
            fetchFreeTrials(),
            fetchUnreadNotificationsCount(),
            fetchPriceData(),
            fetchTrackedPromoMerchants(),
            fetchEstimatedExpense(),
            fetchPremiumStatus()
          ]);
        } catch (error) {
          console.error("Error loading dashboard data:", error);
        } finally {
          setIsLoading(false);
          setInitialLoadComplete(true);
          
          setTimeout(() => {
            setShowContent(true);
          }, 50);
        }
      };
      
      loadData();
    } else if (!user) {
      // User not authenticated
      navigate("/demo");
    } else {
      setIsLoading(false);
      setIsCollectorLoading(false);
      setInitialLoadComplete(true);
      setShowContent(true);
    }

    return () => window.removeEventListener("resize", handleResize);
  }, [user, navigate, isMobile]);

  const handleSubscriptionComplete = async () => {
    try {
      if (!httpClient) return;
      
      await httpClient.request('/api/mark-initial-setup-completed', {
        method: "POST",
      });
      
      setShowSubscriptionCollector(false);
      
      // Refresh BOTH subscriptions and free trials
      await Promise.all([
        checkUserSubscriptions(),
        fetchFreeTrials(),
        fetchTrackedPromoMerchants(),
        fetchEstimatedExpense(),
        fetchPremiumStatus()
      ]);
      
      // Dispatch event to notify other components
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('subscriptionsUpdated'));
      }
    } catch (error) {
      console.error('Error completing subscription:', error);
    }
  };

  // Calculate total expenses from personal subscriptions
  const totalPersonalExpenses = userSubscriptions.reduce((total, sub) => {
    const price = sub.price || 0;
    const monthlyPrice = sub.billing_interval === 'annual' ? price / 12 : price;
    return total + monthlyPrice;
  }, 0);

  // Adjusted total with sharing space estimated expense adjustment
  const adjustedTotalExpenses = totalPersonalExpenses - estimatedExpense.net_adjustment;

  const getSubscriptionData = () => {
    if (userSubscriptions.length === 0) {
      return [
        { name: "Streaming", value: 0 },
        { name: "Productivity", value: 0 },
        { name: "Cloud Storage", value: 0 },
        { name: "Professional", value: 0 }
      ];
    }
  
    const categories = userSubscriptions.reduce((acc, sub) => {
      const category = sub.category || 'Other';
      if (!acc[category]) {
        acc[category] = 0;
      }
      const monthlyValue = sub.billing_interval === 'annual' ? 
        (sub.price || 0) / 12 : 
        (sub.price || 0);
      acc[category] += monthlyValue;
      return acc;
    }, {});
  
    return Object.entries(categories).map(([name, value]) => ({
      name,
      value: parseFloat(value.toFixed(2))
    }));
  };

  const handleToggleNotification = (merchantId, newStatus) => {
    setTrackedPromoMerchants(prev => 
      prev.map(merchant => 
        merchant.id === merchantId 
          ? { ...merchant, notification_status: newStatus }
          : merchant
      )
    );
  };

  if (isMobile) {
    return null;
  }

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Only show SubscriptionCollector if we have valid merchants and initial load is complete */}
      {showSubscriptionCollector && !isCollectorLoading && merchants.length > 0 && initialLoadComplete && (
        <SubscriptionCollector
          userId={user?.userId}
          merchants={merchants}
          onComplete={handleSubscriptionComplete}
          refreshDashboardData={refreshAllData}
        />
      )}
  
      <DashboardSidebar
        isOpen={isSidebarOpen}
        toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
        navItems={navItems}
        currentPath={location.pathname}
        navigate={navigate}
        user={user}
        onLogout={logout}
      />
  
      <div className="flex-1 overflow-auto">
        <DashboardHeader isAuthenticated={!!user} user={user} onLogout={logout} />
        
        <div className="p-6 space-y-4">
          {/* Welcome banner with conditional premium gradient */}
          <div className={`${premiumStatus.isPremium 
            ? 'bg-gradient-to-r from-[#253985] to-amber-500' 
            : 'bg-[#253985]'} text-white p-6 rounded-lg text-center mb-4 transition-all duration-500`}
          >
            <h1 className="text-xl font-semibold mb-2">
              {!initialLoadComplete || isLoading ? t('dashboard.loadingData', 'Loading your data...') : t('dashboard.welcomeTitle', 'Welcome to Your Dashboard')}
            </h1>
            <p className="text-white">
              {!initialLoadComplete || isLoading ? t('dashboard.loadingSubscriptions', 'Please wait while we load your subscriptions') : t('dashboard.manageSubscriptions', 'Manage your subscriptions and view analytics')}
            </p>
          </div>
  
          {/* Show loading spinner only during initial load */}
          {(!initialLoadComplete || isLoading) ? (
            <div className="flex justify-center items-center h-64">
              <Loader2 className="h-12 w-12 animate-spin text-blue-500" />
            </div>
          ) : (
            /* Fade in content when ready */
            <div className={`transition-opacity duration-300 ${showContent ? 'opacity-100' : 'opacity-0'}`}>
              {/* First row - Expenses Overview and Spending by Category */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                  <h2 className="text-lg font-semibold mb-4 text-gray-800">
                    {t('dashboard.expensesOverview', 'Expenses Overview')}
                  </h2>
                  <TotalExpensesCard 
                    total={adjustedTotalExpenses} 
                    originalTotal={totalPersonalExpenses}
                    activeSubscriptions={userSubscriptions.length} 
                    freeTrials={freeTrials} 
                    estimatedExpense={estimatedExpense}
                  />
                </div>
  
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                  <h2 className="text-lg font-semibold mb-4 text-gray-800">
                    {t('dashboard.spendingByCategory', 'Spending by Category')}
                  </h2>
                  <DonutChart
                    data={getSubscriptionData()}
                    category="name"
                    value="value"
                    colors={["#3B82F6", "#10B981", "#F59E0B", "#8B5CF6"]}
                    valueFormatter={(value) => `€${value.toFixed(2)}`}
                    showLabel={true}
                    variant="donut"
                    showLegend={true}
                  />
                </div>
              </div>
  
              {/* Second row - Price Radar and Free Trials */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-800 flex items-center">
                      <div className="relative mr-2">
                        <div className="w-6 h-6 flex items-center justify-center">
                          <div className="absolute w-4 h-4 bg-blue-500 rounded-full opacity-75 animate-ping"></div>
                          <div className="absolute w-4 h-4 bg-blue-600 rounded-full flex items-center justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" className="w-3 h-3">
                              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"/>
                              <path d="M12 6c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6-2.69-6-6-6zm0 10c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4z"/>
                              <path d="M12 10c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
                            </svg>
                          </div>
                        </div>
                      </div>
                      {t('dashboard.priceRadar', 'Price Radar')}
                    </h2>
                  </div>
                  <PriceRadar priceData={priceData} userSubscriptions={userSubscriptions} />
                </div>
                
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-800 flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      {t('dashboard.activeFreeTrials', 'Active Free Trials')}
                    </h2>
                    <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                      {freeTrials.length} {t('dashboard.active', 'active')}
                    </span>
                  </div>
                  <FreeTrialList 
                    freeTrials={freeTrials} 
                    refreshFreeTrials={fetchFreeTrials} 
                  />
                </div>
              </div>
  
              {/* Your Subscriptions section */}
              <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100 mt-4">
                <h3 className="text-lg font-medium mb-4">
                  {t('dashboard.yourSubscriptions', 'Your Subscriptions')}
                </h3>
                {userSubscriptions.length > 0 ? (
                  <SubscriptionList subscriptions={userSubscriptions} />
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-500">
                      {t('dashboard.noSubscriptions', 'No subscriptions found')}
                    </p>
                    <button
                      onClick={() => navigate("/marketplace")}
                      className="mt-4 bg-[#253985] hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                      {t('dashboard.addFirstSubscription', 'Add Your First Subscription')}
                    </button>
                  </div>
                )}
              </div>

              {/* Tracked Merchants for Promo Notifications section */}
              <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100 mt-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                    <h3 className="text-lg font-medium">
                      {t('dashboard.trackedMerchantsForPromos', 'Tracked Merchants for Promos')}
                    </h3>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-600">
                      {trackedPromoMerchants.length} {t('dashboard.tracked', 'tracked')}
                    </span>
                    <button
                      onClick={() => navigate("/marketplace")}
                      className="flex items-center gap-2 bg-[#253985] hover:bg-[#1a2a6d] text-white px-4 py-2 rounded-lg transition-colors text-sm"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      {t('dashboard.addMoreMerchants', 'Add More')}
                    </button>
                  </div>
                </div>
                
                {trackedPromoMerchants.length > 0 ? (
                  <TrackedPromoMerchantsList 
                    trackedMerchants={trackedPromoMerchants}
                    onToggleNotification={handleToggleNotification}
                  />
                ) : (
                  <div className="text-center py-8 bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl border border-gray-200">
                    <div className="mx-auto w-24 h-24 bg-white rounded-full flex items-center justify-center shadow-inner mb-4">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-[#253985]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                      </svg>
                    </div>
                    <h4 className="text-lg font-medium text-gray-800 mb-2">
                      {t('dashboard.noTrackedMerchants', 'No tracked merchants yet')}
                    </h4>
                    <p className="text-gray-600 mb-4 max-w-md mx-auto">
                      {t('dashboard.trackMerchantsDescription', 'Track merchants to get notified about promotions, discounts, and special offers')}
                    </p>
                    <div className="flex justify-center gap-4">
                      <button
                        onClick={() => navigate("/marketplace")}
                        className="bg-[#253985] hover:bg-[#1a2a6d] text-white px-6 py-3 rounded-lg transition-colors"
                      >
                        {t('dashboard.browseMerchants', 'Browse Merchants')}
                      </button>
                      <button
                        onClick={() => navigate("/notifications?tab=promos")}
                        className="bg-purple-100 hover:bg-purple-200 text-purple-700 px-6 py-3 rounded-lg transition-colors border border-purple-200"
                      >
                        {t('dashboard.viewPromoAlerts', 'View Promo Alerts')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
  
          {/* Connect bank banner */}
          <div className={`sticky bottom-6 mt-4 bg-white p-4 rounded-xl shadow-md border border-gray-200 transition-opacity duration-300 ${showContent ? 'opacity-100' : 'opacity-0'}`}>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-gray-800">
                  {t('dashboard.connectBank', 'Connect Your Bank')}
                </h3>
                <p className="text-sm text-gray-500">
                  {t('dashboard.autoTrackSubscriptions', 'Automatically track all subscriptions')}
                </p>
              </div>
              <button
                onClick={() => navigate("/connectBank")}
                className="bg-[#253985] hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                {t('dashboard.connectNow', 'Connect Now')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
  
export default Dashboard;